import type { Commodity } from '@enmon/commons/lib/shared';
import { useMemo } from 'react';
import {
  createMeterModelCatalogItemFromBootstrapModel,
  type MeterModelCatalogItem,
} from '@/features/inventorization/meter-model-catalog-client';
import type { InventoryBuildingOption } from '@/features/inventorization/new-meter-location-client';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import type {
  LocationBootstrapBuilding,
  LocationBootstrapThirdParty,
} from '@/features/location-bootstrap/location-bootstrap-types';

export function useReviewFieldOptions(selectedManufacturer: string, selectedCommodity: Commodity | '') {
  const { bootstrap } = useLocationBootstrap();
  const buildingOptions = useMemo(
    () => deriveBuildingOptionsFromBootstrap(bootstrap?.buildings ?? []),
    [bootstrap?.buildings],
  );
  const meterModels = useMemo<MeterModelCatalogItem[]>(
    () => (bootstrap?.meterModels ?? []).map(createMeterModelCatalogItemFromBootstrapModel),
    [bootstrap?.meterModels],
  );
  const filteredMeterModels = useMemo(() => {
    return selectedCommodity ? meterModels.filter(model => model.commodity === selectedCommodity) : meterModels;
  }, [meterModels, selectedCommodity]);

  const manufacturerOptions = useMemo(() => {
    const options = new Map<string, { label: string; value: string }>();

    for (const model of filteredMeterModels) {
      if (model.manufacturerId && model.manufacturerName) {
        options.set(model.manufacturerId, { label: model.manufacturerName, value: model.manufacturerId });
      }
    }

    return [...options.values()].toSorted((left, right) => left.label.localeCompare(right.label));
  }, [filteredMeterModels]);

  const manufacturerMeterModels = useMemo(() => {
    const normalizedManufacturer = selectedManufacturer.trim().toLowerCase();

    return filteredMeterModels.filter(
      model =>
        !normalizedManufacturer ||
        model.manufacturerName.toLowerCase() === normalizedManufacturer ||
        model.manufacturerId.toLowerCase() === normalizedManufacturer,
    );
  }, [filteredMeterModels, selectedManufacturer]);

  const modelOptions = useMemo(() => {
    return manufacturerMeterModels
      .map(model => ({ label: model.name, value: model.id }))
      .toSorted((left, right) => left.label.localeCompare(right.label));
  }, [manufacturerMeterModels]);
  const tenantOptions = useMemo(
    () => (bootstrap?.tenants ?? []).map(tenant => ({ label: tenant, value: tenant })),
    [bootstrap],
  );
  const ownerSuggestions = useMemo(
    () => (bootstrap?.ownerSuggestions ?? []).map(toOption),
    [bootstrap?.ownerSuggestions],
  );
  const utilityCompanyOwnerOptions = useMemo(
    () => deriveUtilityCompanyOwnerOptions(bootstrap?.thirdParties ?? [], selectedCommodity),
    [bootstrap?.thirdParties, selectedCommodity],
  );

  return {
    buildingOptions,
    manufacturerOptions,
    meterModels: manufacturerMeterModels,
    modelOptions,
    ownerSuggestions,
    tenantOptions,
    utilityCompanyOwnerOptions,
  };
}

function toOption(value: string) {
  return { label: value, value };
}

function deriveUtilityCompanyOwnerOptions(thirdParties: LocationBootstrapThirdParty[], commodity: Commodity | '') {
  return thirdParties
    .filter(thirdParty => thirdParty.commodities.some(com => commodity === com))
    .map(thirdParty => toOption(thirdParty.name))
    .toSorted((left, right) => left.label.localeCompare(right.label));
}

function deriveBuildingOptionsFromBootstrap(buildings: LocationBootstrapBuilding[]): InventoryBuildingOption[] {
  return buildings
    .map(building => ({
      code: building.code,
      id: String(building.id),
      name: building.name,
    }))
    .toSorted((left, right) => left.name.localeCompare(right.name));
}
